import {
    R6PayApiErrorResponse,
    R6PayApiSuccessResponse,
} from '@r6digital/r6pay-types/types/Api';
import PaymentMethod from '@r6digital/r6pay-types/types/PaymentMethod';
import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import {
    Action,
    CreatePaymentLinkPayload,
    CreatePaymentLinkResponse,
    Mode,
} from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import Location, {
    SupportedCurrencyCodes,
} from '@r6digital/rapidstor-types/react/entities/Location';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { HttpMethod } from '@/core/types/Http';

import BaseService from '@/features/rapidstor/services/BaseService';

export interface StripePaymentIntent {
    clientSecret: string;
    publishableKey: string;
    paymentLinkId: string;
    returnUrl: string;
}

export interface CreatePaymentLinkPayloadOptions {
    reservation: BaseReservation;
    location: Location;
    currency: SupportedCurrencyCodes;
    returnUrl: string;
    paymentMethod: PaymentProcessor;
    /**
     * Move-in form values. When supplied (and the payment is not a reservation
     * fee) the contact details captured on the form are used for the sender,
     * otherwise the details already stored on the reservation are used.
     */
    form?: Record<string, string>;
    /** Set for a reservation fee payment; otherwise the payload covers move-in costs. */
    reservationFee?: number;
}

export default class R6PayService extends BaseService {
    r6PayBaseUrl: string;

    rapidstorBaseUrl: string;

    accessToken: string;

    temporaryToken?: string;

    headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    paymentMethods: PaymentMethod[] = [];

    constructor(location: Location, isDevelopment?: boolean) {
        super(location);
        this.r6PayBaseUrl = isDevelopment
            ? (import.meta.env.VITE_R6PAY_BASE_URL ?? 'https://r6-pay.r6staging.com')
            : 'https://pay.r6clients.com';
        this.rapidstorBaseUrl = isDevelopment
            ? (import.meta.env.VITE_DEV_BASE_URL ?? 'https://api.rapidstorapp.com')
            : 'https://prod.rapidstorapp.com';
        if (!location.rapidstor.paymentGateways.gateways.r6Pay) {
            throw new Error('R6Pay gateway not configured for this location');
        }
        this.accessToken = location.rapidstor.paymentGateways.gateways.r6Pay.accessToken;
        this.headers.Authorization = `Bearer ${this.accessToken}`;
    }

    /**
   *
   * HELPER OR SERVICE FUNCTIONS
   *
   */
    dollarsToCents(dollars: number | string): number {
        if (typeof dollars === 'number') {
            return Math.round(100 * dollars);
        }
        return Math.round(100 * parseFloat(dollars.replace(/[$,]/g, '')));
    }

    /**
     * Builds the R6Pay create payment link payload for every gateway, including
     * Stripe Direct.
     */
    buildCreatePaymentLinkPayload({
        reservation,
        location,
        currency,
        returnUrl,
        paymentMethod,
        form,
        reservationFee,
    }: CreatePaymentLinkPayloadOptions): CreatePaymentLinkPayload {
        // A zero fee is still a reservation fee payment, so the mode is decided by
        // whether a fee was passed at all — never by its value.
        const isReservationFee = reservationFee !== undefined;

        const amount = reservationFee !== undefined
            ? reservationFee
            : (reservation.moveInContext.movein.paymentAmount ?? 0)
            + (reservation.additionalAmount ?? 0);

        const tenant = !isReservationFee && form
            ? {
                email: form.email,
                firstName: form.firstName,
                lastName: form.lastName,
            }
            : {
                email: reservation.email,
                firstName: reservation.firstName,
                lastName: reservation.lastName,
            };

        const action = isReservationFee ? Action.RESERVATION_WITH_FEE : Action.MOVE_IN;
        const channel = `RapidStor ${isReservationFee ? 'Reservation-With-Fee' : 'Move-In'} (V2)`;
        const uniqueReferenceId = `${reservation.tenantId}_${Date.now() / 1000}`;
        const webhookUrl = `${this.rapidstorBaseUrl}/api/v1/r6pay/webhook`;
        const paymentDetails = {
            description: isReservationFee ? 'Reservation Fee' : 'Move In Costs',
            external_transaction_reference: `${reservation.tenantId}`,
        };

        return {
            action,
            amount: this.dollarsToCents(amount),
            payment_details: paymentDetails,
            sender: {
                email: tenant.email,
                name: `${tenant.firstName} ${tenant.lastName}`,
                external_user_id: `${reservation.tenantId}`,
            },
            mode: Mode.PAYMENT,
            payment_method: paymentMethod,
            currency,
            status: 'pending',
            timestamp: new Date(),
            return_url: returnUrl,
            webhook_url: webhookUrl,
            metadata: {
                action,
                originUrl: window.location.href,
                corp: location.corpCode,
                site: location.locationCode,
                channel,
                location: location.locationCode,
                locationHash: window.location.hash,
                tenant: `${reservation.tenantId}`,
                tenantId: `${reservation.tenantId}`,
                unique_reference_id: uniqueReferenceId,
                reservationId: `${reservation._id}`,
                frontend_url: window.location.href,
                payment_details: paymentDetails,
                return_url: returnUrl,
                payment_metadata: {
                    sCorpCode: location.corpCode,
                    sLocationCode: location.locationCode,
                    friendlyName: location.name,
                    SiteId: `${location.locationId}`,
                    tenantId: `${reservation.tenantId}`,
                    unique_reference_id: uniqueReferenceId,
                    channel,
                },
                webhook_url: webhookUrl,
            },
        };
    }

    async getPaymentMethods() {
        if (this.paymentMethods.length) {
            return this.paymentMethods;
        }

        const response = await fetch(
            `${this.r6PayBaseUrl}/api/account/payment_methods`,
            {
                method: HttpMethod.GET,
                headers: this.headers,
            },
        );

        if (!response.ok) {
            throw new Error('Failed to get payment methods from R6Pay', {
                cause: response,
            });
        }

        const result = (await response.json()) as R6PayApiSuccessResponse<
        PaymentMethod[]
        >;

        this.paymentMethods = result.data;

        return result.data;
    }

    async createPaymentLink(payload: CreatePaymentLinkPayload) {
        const response = await fetch(
            `${this.r6PayBaseUrl}/api/payment/link/create`,
            {
                method: HttpMethod.POST,
                headers: this.headers,
                body: JSON.stringify(payload),
            },
        );
        const parsedResponse = await response.json();

        if (!response.ok) {
            const errorResponse = parsedResponse as R6PayApiErrorResponse;
            this.handleError(
                '[R6PayService] Failed to create payment link',
                JSON.stringify(errorResponse.errors),
                [
                    {
                        name: 'external_transaction_reference',
                        value: payload.payment_details.external_transaction_reference,
                    },
                    {
                        name: 'sender',
                        value: JSON.stringify(payload.sender),
                    },
                    {
                        name: 'tenant',
                        value: payload.metadata.tenant,
                    },
                ],
            );
            throw new Error(Array.isArray(errorResponse.errors) ? errorResponse.errors.join(', ') : errorResponse.errors);
        }

        const result = parsedResponse as R6PayApiSuccessResponse<CreatePaymentLinkResponse>;

        return result.data;
    }

    postToUrl(url: string, formData: Record<string, string>) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;

        for (const [key, value] of Object.entries(formData)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();

        setTimeout(() => {
            if (form.parentNode) {
                document.body.removeChild(form);
            }
        }, 2000);
    }
}
